import { getSelectedPointId } from './PointSelector';
import {
  SYNC_KEYS,
  SHIFT_PERSIST_KEYS,
  REFERENCE_KEYS,
  onSyncedKeyChange,
  collectPointState,
  collectReferenceState,
  collectShiftState,
  prepareShiftPayloadForServer,
  getActivePointId,
} from './pointStorage';

let saveTimer = null;
let liveTimer = null;
let shiftSyncTimer = null;
let shiftSyncRunning = false;
let shiftSyncPromise = null;
let shiftSyncPending = false;
let queuedExplicitShift = null;
let unsubscribeChange = null;
let activePointId = null;
let syncReadyAt = 0;
let shiftSyncEnabled = false;
const SYNC_WARMUP_MS = 0;
const pendingRefKeys = new Set();

export function enableShiftSync() {
  shiftSyncEnabled = true;
  syncReadyAt = Date.now() + SYNC_WARMUP_MS;
}

function collectLiveSnapshot() {
  const shiftOpen = localStorage.getItem('shiftStatus') === 'Открыта';
  let photographers = [];
  let retouchers = [];
  let sales = [];
  let salesCount = 0;
  let totalSales = 0;
  let totalCash = 0;
  let totalCard = 0;
  let totalTransfer = 0;

  try {
    photographers = JSON.parse(localStorage.getItem('photographersInShift') || '[]').map((p) => p.label || p);
  } catch {}
  try {
    retouchers = JSON.parse(localStorage.getItem('retouchersInShift') || '[]').map((r) => r.label || r);
  } catch {}
  try {
    sales = JSON.parse(localStorage.getItem('salesData') || '[]');
    salesCount = sales.length;
    sales.forEach((s) => {
      const cash = Number(s.cash) || 0;
      const card = Number(s.card) || 0;
      const transfer = Number(s.transfer) || 0;
      totalCash += cash;
      totalCard += card;
      totalTransfer += transfer;
      totalSales += cash + card + transfer;
    });
  } catch {}

  const salesDetails = sales.map((s) => ({
    photographer: s.photographer || '—',
    product: s.product || '—',
    quantity: s.quantity || 0,
    cash: Number(s.cash) || 0,
    card: Number(s.card) || 0,
    transfer: Number(s.transfer) || 0,
    total: (Number(s.cash) || 0) + (Number(s.card) || 0) + (Number(s.transfer) || 0),
    time: s.time || null,
  }));

  return {
    shiftOpen,
    photographers,
    retouchers,
    totalSales,
    salesCount,
    totalCash,
    totalCard,
    totalTransfer,
    salesDetails,
    pointId: getActivePointId(),
  };
}

function postJson(url, body, { keepalive = false } = {}) {
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive,
  });
}

function putJson(url, body, { keepalive = false } = {}) {
  return fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive,
  });
}

async function pushState(pointId, keys, { keepalive = false } = {}) {
  const state = keys ? collectReferenceState(keys) : collectPointState();
  if (!Object.keys(state).some((k) => k !== '_pointId')) return;
  const res = await putJson(`/api/points/${pointId}/state`, state, { keepalive });
  if (!res.ok) throw new Error(`state sync failed: ${res.status}`);
}

export function cancelPendingReferenceSave() {
  clearTimeout(saveTimer);
  saveTimer = null;
  pendingRefKeys.clear();
}

async function pushShift(pointId, shift, { keepalive = false } = {}) {
  const payload = shift ? prepareShiftPayloadForServer(shift) : collectShiftState();
  const res = await putJson(`/api/points/${pointId}/shift`, payload, { keepalive });
  if (!res.ok) throw new Error(`shift sync failed: ${res.status}`);
}

async function pushLive(pointId, { keepalive = false } = {}) {
  const live = collectLiveSnapshot();
  const res = await postJson(`/api/points/${pointId}/live`, live, { keepalive });
  if (!res.ok) throw new Error(`live sync failed: ${res.status}`);
}

function canAutoShiftSync() {
  return Date.now() >= syncReadyAt;
}

async function runShiftSyncLoop(pointId, { keepalive = false, shift } = {}) {
  let latestShift = shift;

  do {
    shiftSyncPending = false;
    await pushShift(pointId, latestShift, { keepalive });
    latestShift = queuedExplicitShift;
    if (latestShift) queuedExplicitShift = null;
    try {
      await pushLive(pointId, { keepalive });
    } catch (error) {
      console.warn(error);
    }
  } while (shiftSyncPending || queuedExplicitShift);
}

async function doShiftSync({ keepalive = false, shift, force = false } = {}) {
  const pointId = activePointId || getActivePointId() || getSelectedPointId();
  if (!pointId) return;
  if (!shiftSyncEnabled && !keepalive && !shift && !force) return;

  if (shift) {
    queuedExplicitShift = shift;
  }

  shiftSyncPending = true;

  if (shiftSyncRunning) {
    return shiftSyncPromise;
  }

  shiftSyncRunning = true;
  const initialShift = queuedExplicitShift;
  if (initialShift) queuedExplicitShift = null;

  shiftSyncPromise = runShiftSyncLoop(pointId, { keepalive, shift: initialShift })
    .catch((error) => {
      console.warn(error);
      throw error;
    })
    .finally(() => {
      shiftSyncRunning = false;
      shiftSyncPromise = null;
    });

  return shiftSyncPromise;
}

export function syncShiftToServer({ immediate = false, keepalive = false, shift, force = false } = {}) {
  const runNow = immediate || keepalive || shift || force;
  if (runNow) {
    clearTimeout(shiftSyncTimer);
    shiftSyncTimer = null;
    return doShiftSync({ keepalive, shift, force: runNow });
  }

  clearTimeout(shiftSyncTimer);
  return new Promise((resolve, reject) => {
    shiftSyncTimer = setTimeout(() => {
      shiftSyncTimer = null;
      doShiftSync().then(resolve).catch(reject);
    }, 300);
  });
}

/** Сохранить только указанные справочники точки (без затирания остальных ключей на сервере). */
export async function syncReferenceKeysToServer(keys, { keepalive = false } = {}) {
  const pointId = activePointId || getActivePointId() || getSelectedPointId();
  if (!pointId) return;
  const keyList = (Array.isArray(keys) ? keys : [keys]).filter(Boolean);
  if (!keyList.length) return;
  cancelPendingReferenceSave();
  await pushState(pointId, keyList, { keepalive });
}

/** Сохранить все справочники точки */
export async function syncReferenceToServer({ keepalive = false } = {}) {
  return syncReferenceKeysToServer(REFERENCE_KEYS, { keepalive });
}

/** Сохранить ВСЁ состояние точки: настройки + смена + live-мониторинг */
export async function syncPointToServer({ keepalive = false, shift } = {}) {
  const pointId = activePointId || getActivePointId() || getSelectedPointId();
  if (!pointId) return;
  await pushState(pointId, null, { keepalive });
  await pushShift(pointId, shift, { keepalive });
  await pushLive(pointId, { keepalive });
}

export async function forceServerSyncNow(opts = {}) {
  return syncPointToServer(opts);
}

export function startServerSync() {
  if (window.electron) return () => {};

  const pointId = getSelectedPointId();
  if (!pointId || getActivePointId() !== pointId) return () => {};

  activePointId = pointId;

  const scheduleReferenceSave = (key) => {
    pendingRefKeys.add(key);
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      const keys = [...pendingRefKeys];
      pendingRefKeys.clear();
      syncReferenceKeysToServer(keys).catch(console.warn);
    }, 300);
  };

  unsubscribeChange = onSyncedKeyChange((key) => {
    if (!SYNC_KEYS.includes(key)) return;
    if (!canAutoShiftSync()) return;
    if (REFERENCE_KEYS.includes(key)) {
      scheduleReferenceSave(key);
      return;
    }
    if (SHIFT_PERSIST_KEYS.includes(key)) {
      doShiftSync({ force: true }).catch(console.warn);
    }
  });

  const saveOnLeave = () => {
    if (pendingRefKeys.size) {
      const keys = [...pendingRefKeys];
      pendingRefKeys.clear();
      clearTimeout(saveTimer);
      saveTimer = null;
      syncReferenceKeysToServer(keys, { keepalive: true }).catch(console.warn);
    }
    doShiftSync({ keepalive: true, force: true }).catch(console.warn);
  };

  window.addEventListener('beforeunload', saveOnLeave);
  window.addEventListener('pagehide', saveOnLeave);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') saveOnLeave();
  });

  // Только live-снимок для мониторинга. Смену (/shift) пишем при явных изменениях,
  // иначе интервал перезаписывает сервер устаревшими данными из localStorage.
  liveTimer = setInterval(() => {
    pushLive(pointId).catch(console.warn);
  }, 3000);

  return () => {
    clearTimeout(saveTimer);
    clearTimeout(shiftSyncTimer);
    clearInterval(liveTimer);
    window.removeEventListener('beforeunload', saveOnLeave);
    window.removeEventListener('pagehide', saveOnLeave);
    if (unsubscribeChange) unsubscribeChange();
    activePointId = null;
  };
}

export function isWebMultiPointMode() {
  return !window.electron && typeof window !== 'undefined';
}
